import type { ActiveContext } from '../types';

function normalizeWhitespace(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

export function isAmbiguousDocumentCountFollowUp(query: string, activeContext?: ActiveContext): boolean {
  if (activeContext?.mode !== 'document') return false;

  const normalized = normalizeWhitespace(query).toLowerCase();
  if (!normalized) return false;

  const referentialPatterns = [
    /^(are|is|does|do)\s+(these|this|that|those)\s+all\b/,
    /^(are|is|does|do)\s+(these|this|that|those|they)\s+all\s+\d+\b/,
    /\ball\s+\d+\s+rtus?\b/,
    /\ball of (them|those|these)\b/,
  ];

  return referentialPatterns.some((pattern) => pattern.test(normalized));
}

export function buildDocumentFollowUpClarification(
  query: string,
  conversationHistory: Array<{ role: string; content: string }> = [],
  activeContext?: ActiveContext
): string | undefined {
  if (!isAmbiguousDocumentCountFollowUp(query, activeContext)) {
    return undefined;
  }

  const latestAssistant = [...conversationHistory]
    .reverse()
    .find((message) => message.role === 'assistant' && typeof message.content === 'string' && message.content.trim().length > 0);

  const latestAssistantHint = latestAssistant?.content
    ? normalizeWhitespace(latestAssistant.content).slice(0, 220)
    : undefined;

  const parts = [
    'The user is asking whether the counts, rows, or units referenced in the immediately previous document-analysis answer cover the full uploaded RTU dataset.',
    'Interpret pronouns like "these", "this", or "that" as referring to the prior assistant answer in this same document workflow.',
    'Interpret phrases like "all 800" as asking whether the answer includes all 800 RTUs from the uploaded workbook, not whether 800 is a model number, tonnage, serial fragment, or other attribute search.',
  ];

  if (activeContext?.lastTask) {
    parts.push(`Most recent document task: ${normalizeWhitespace(activeContext.lastTask)}.`);
  }

  if (latestAssistantHint) {
    parts.push(`Previous assistant answer excerpt: ${latestAssistantHint}`);
  }

  return parts.join(' ');
}
